/**
 * 新着ポストのピル (X の「◯件のポスト」バナー、2026-08-25)
 *
 * X はタイムラインを見ている間に新しい投稿が来ると、上部に
 * 「投稿者のアバター + N 件の新着ポスト」のピルが出て、押すと最上部へ移動して
 * 新着が読み込まれる。当サイトは引っ張って更新 (pull_refresh.ts) しか無く、
 * PC では新着に気付く手段が無かった。
 *
 * サーバ負荷: 60 秒ごとに全クライアントが叩くので、ポーリングは
 * `count_only=1` を付けて **post_card のレンダリング (最大 30 枚) と公共注釈の
 * populate を省く** (timeline_refresh.go)。実際に差し込む時だけ HTML を取る。
 *
 * ⚠ 差し込みロジックは pull_refresh.ts の handleAjaxDone と同じ規則
 *   (ピン留めの下へ / 既存 ID は弾く) を踏襲している。あちらは
 *   initPullRefresh のクロージャ内にあり、タッチ非対応端末では初期化ごと
 *   早期 return するため再利用できない。**片方を直したらもう片方も直すこと。**
 */

import { u } from "./url";
import { apiFetch, type TimelineRefreshResp } from "./api";
import { topSlot, follow, measure, showResult, TN_ICON } from "./topnotice";
import { landNewPosts } from "./new-posts-motion";

const POLL_MS = 60_000;
const MAX_AVATARS = 3;
/** timeline_refresh.go が返す上限。これ以上あるかは分からないので「以上」と言う */
const REFRESH_CAP = 30;

let pill: HTMLButtonElement | null = null;
let timer: number | null = null;
let busy = false;
/** 上へ抜けて消える動きの最中 (timeline-prepended で即座に消さない)。 */
let exiting = false;

/** ホームのタイムラインか。
 *  URL ではなく **DOM の目印** (#timeline[data-tab] は home.html だけが出す) で
 *  判定する。softnav は .col-main を差し替えるだけでページ遷移しないので、
 *  「ホームから離れたらポーリングを止め、戻ったら再開する」がこれだけで成立する。 */
function homeTimelineEl(): HTMLElement | null {
  return document.querySelector<HTMLElement>("#timeline[data-tab]");
}

function isHomeTimeline(): boolean {
  return homeTimelineEl() !== null;
}

/** 表示中の (ピン留めでない) 最大 post ID。ピンは古い ID を持つので除外する。 */
function currentSinceId(): number {
  let sinceId = 0;
  document.querySelectorAll<HTMLElement>(".post[data-post-id]").forEach((el) => {
    if (el.querySelector(".post-pinned-label")) return;
    const n = parseInt(el.getAttribute("data-post-id") || "0", 10);
    if (n > sinceId) sinceId = n;
  });
  return sinceId;
}

function currentTab(): string {
  const el = homeTimelineEl();
  const fromDom = el ? el.getAttribute("data-tab") : "";
  if (fromDom) return fromDom;
  return new URLSearchParams(window.location.search).get("tab") || "foryou";
}

function refreshPath(countOnly: boolean): string {
  return (
    "/api/v1/timeline/refresh?since_id=" +
    currentSinceId() +
    "&tab=" +
    encodeURIComponent(currentTab()) +
    (countOnly ? "&count_only=1" : "")
  );
}

/** ピン留めでない先頭の投稿 (= 新着の差し込み位置)。 */
function findFirstPost(): HTMLElement | null {
  const all = document.querySelectorAll<HTMLElement>(".post[data-post-id]");
  for (let i = 0; i < all.length; i++) {
    if (!all[i].querySelector(".post-pinned-label")) return all[i];
  }
  return null;
}

function ensurePill(): HTMLButtonElement {
  if (pill && pill.isConnected) return pill;
  // 2026-09-21「上に出る知らせ」G 案: 押せる新着だけは紫で塗る。置き場所はタブの下の層
  // (topnotice.ts) — ヘッダーの出入りに付いていく (旧実装は出た瞬間の位置のままだった)。
  const el = document.createElement("button");
  el.type = "button";
  el.className = "np-pill";
  el.hidden = true;
  el.innerHTML = '<span class="np-pill-in">' + TN_ICON.up + '<span class="np-pill-avatars"></span><span class="np-pill-label"></span></span>';
  el.addEventListener("click", () => void loadNewPosts());
  topSlot().appendChild(el);
  pill = el;
  return el;
}

let shown = false;
function hidePill(): void {
  if (pill) {
    pill.hidden = true;
    pill.classList.remove("is-in");
  }
  if (shown) { shown = false; follow(false); }
}

/** 届いたとき: §256 (上に出る知らせ) の決まりどおり、is-in を外して上へ少し抜けながら
 *  薄れさせ、消えきってから隠す (いきなり hidden にすると一瞬で消えて見える)。 */
function exitPill(after?: () => void): void {
  if (!pill || pill.hidden) {
    if (after) after();
    return;
  }
  exiting = true;
  pill.classList.remove("is-in");
  window.setTimeout(() => {
    exiting = false;
    if (after) after();
    hidePill();
  }, 300);
}

function showPill(data: TimelineRefreshResp): void {
  const el = ensurePill();
  const label = el.querySelector<HTMLElement>(".np-pill-label");
  const avatars = el.querySelector<HTMLElement>(".np-pill-avatars");
  const n = data.count >= REFRESH_CAP ? REFRESH_CAP + " 件以上" : data.count + " 件";
  if (label) label.textContent = "新しいポスト " + n;
  el.setAttribute("aria-label", "新しいポスト " + n + "を表示");
  if (avatars) {
    avatars.innerHTML = "";
    const seen = new Set<string>();
    (data.posts || []).forEach((p) => {
      if (avatars.childElementCount >= MAX_AVATARS || seen.has(p.handle)) return;
      seen.add(p.handle);
      const a = document.createElement("span");
      a.className = "np-pill-av";
      // 画像を設定していない人は既定の絵 (internal/defavatar、どのテーマでも同じ絵)
      const img = document.createElement("img");
      img.src = p.avatar
        ? u("/img/64/" + String(p.avatar).replace(/^\/?uploads\//, ""))
        : u("/avatar/default/" + encodeURIComponent(p.handle || ""));
      img.alt = "";
      a.appendChild(img);
      avatars.appendChild(a);
    });
  }
  el.hidden = false;
  if (!shown) { shown = true; follow(true); }
  measure();
  // hidden を外した直後に class を足しても transition が走らないので 1 フレーム待つ。
  // rAF は背景タブや一部のヘッドレスで発火しないことがある ([[headless-virtual-time-raf-trap]])
  // ので、保険の setTimeout も置く (二重に付けても実害なし)。
  requestAnimationFrame(() => el.classList.add("is-in"));
  window.setTimeout(() => el.classList.add("is-in"), 50);
}

/** ピルを押した時: 本体を取りに行って先頭へ差し込み、最上部へ戻す。 */
async function loadNewPosts(): Promise<void> {
  if (busy) return;
  busy = true;
  // 取りに行っている間はアバターの代わりにネオンの輪 (v2.css §217)。
  const pillEl = document.querySelector<HTMLElement>(".np-pill");
  const spin = document.createElement("span");
  spin.className = "mp-spin";
  spin.setAttribute("aria-hidden", "true");
  const labelEl = pillEl ? pillEl.querySelector<HTMLElement>(".np-pill-label") : null;
  const labelWas = labelEl ? labelEl.textContent || "" : "";
  if (pillEl) {
    pillEl.classList.add("is-loading");
    const inner = pillEl.querySelector(".np-pill-in") || pillEl;
    inner.insertBefore(spin, inner.firstChild);
    if (labelEl) labelEl.textContent = "読み込み中";
  }
  let failed = false;
  let exited = false;
  /** 読み込み中の見た目を戻す (消えていく途中で「読み込み中」→元の文に戻ってちらつかないよう、
   *  届いたときは消えきってから) */
  const restore = (): void => {
    if (pillEl) { pillEl.classList.remove("is-loading"); spin.remove(); }
    if (labelEl) labelEl.textContent = labelWas;
  };
  try {
    const data = await apiFetch<TimelineRefreshResp>(refreshPath(false));
    const firstPost = findFirstPost();
    const parent = firstPost ? firstPost.parentNode : null;
    if (!data.count || !data.html || !firstPost || !parent) {
      hidePill();
      return;
    }
    const existing = new Set<string>();
    document.querySelectorAll<HTMLElement>(".post[data-post-id]").forEach((el) => {
      const id = el.getAttribute("data-post-id");
      if (id) existing.add(id);
    });
    const tmp = document.createElement("div");
    tmp.innerHTML = data.html;
    // サーバは新しい順で返す。firstPost の直前へ順に入れると
    // [新, …, 旧, 既存先頭] になり、ピン留めの上には積まれない。
    const inserts = Array.from(tmp.children).filter((node) => {
      const id = node.getAttribute("data-post-id");
      return !(id && existing.has(id));
    });
    if (inserts.length > 0) {
      // ボタンは上へ抜けて薄れ、見ている位置を保ったまま上に差し込み、先頭まで滑って戻る。
      // 新しい投稿は戻る途中から上から順に現れる (new-posts-motion.ts / v2.css §255)。
      exited = true;
      exitPill(restore);
      landNewPosts(parent, firstPost, inserts, 0);
      try { window.dispatchEvent(new Event("timeline-prepended")); } catch (_) { /* ignore */ }
    } else {
      hidePill();
    }
  } catch (_) {
    // 旧実装は黙ってピルを消していた。読めなかったことは言い、もう一度を置く
    failed = true;
    hidePill();
  } finally {
    busy = false;
    if (!exited) restore();
  }
  if (failed) showResult("fail", 0, () => void loadNewPosts());
}

async function poll(): Promise<void> {
  if (document.visibilityState !== "visible" || !isHomeTimeline() || busy) return;
  if (currentSinceId() <= 0) return; // タイムラインがまだ無い
  try {
    const data = await apiFetch<TimelineRefreshResp>(refreshPath(true));
    if (data && data.count > 0) showPill(data);
    else hidePill();
  } catch (_) {
    /* 通信エラーは黙って次の周期へ */
  }
}

export function initNewPostsPill(): void {
  // ここでホーム判定はしない。softnav でホームへ入った時にも効くよう、
  // 判定は毎回の poll() 側で行う (タイマー自体は常駐させて構わない軽さ)。
  if (timer !== null) window.clearInterval(timer);
  timer = window.setInterval(() => void poll(), POLL_MS);
  // タブに戻ってきた時は待たずに 1 回見る (X も復帰時にすぐ出る)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void poll();
  });
  // ★ 開いた直後にも 1 回見る (2026-09-21 オーナー指示「新着 (N 件の新着ポスト) が開けた時に
  // 表示されないとだめ」)。おすすめは前回いちばん上だった束から表示し、それより新しい投稿は
  // 押すまで上に溜めておく (home_foryou.go) ので、開いた瞬間に件数が出ないと気付けない。
  // softnav でホームへ戻ってきたとき (mp:navigated) も同じ。60 秒の周期は待たない。
  window.setTimeout(() => void poll(), 300);
  document.addEventListener("mp:navigated", () => window.setTimeout(() => void poll(), 300));
  // 自分で投稿した / 引っ張って更新した直後はピルを消す (もう最新)
  window.addEventListener("timeline-prepended", () => {
    if (!exiting) hidePill();
  });
}
